/**
 * useLanguageOptions
 *
 * Returns the languages offered by the language selector, sorted alphabetically
 * by their native name (endonym) — the single owner of "which languages are
 * offered, in what order", so the rendering component never re-implements it.
 *
 * **Web-only — there is no Flutter counterpart.** The Flutter app ships a single
 * hardcoded language and has no language picker or localization layer, so there
 * is no ordering behaviour to keep parity with; this is a web UI decision.
 *
 * Notes:
 * - The spread copy is required: `Array.prototype.sort` mutates in place and
 *   `locales` is a shared module constant whose declaration order other
 *   consumers may rely on.
 * - Sorting on `nativeName` (not `name`) because the endonym is the row's
 *   primary label — the string the user actually scans.
 * - `localeCompare` rather than `<` / `>` so diacritics collate next to their
 *   base letter (`Română` sorts with `R…`, not after `Z`).
 * - The empty dependency array is correct: `locales` is a module constant, not
 *   reactive state.
 *
 * Returns raw `LocaleOption`s and reads no `l10n` — the labels are the
 * languages' own endonym/exonym data, not translated copy.
 */

import { useMemo } from 'react';
import { locales, type LocaleOption } from '@/presentation/locale';

export const useLanguageOptions = (): LocaleOption[] =>
  useMemo(
    () => [...locales].sort((a, b) => a.nativeName.localeCompare(b.nativeName)),
    [],
  );
